<script setup lang="ts">
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { NightsSelector } from '@apps/main/new-desktop/modules/nightsSelector';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';

import { AppSvgIcon, AppTrashCanButton } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  ItineraryLocationLoaderSegment,
  ItineraryLocationSegment,
} from '../../../models';
import { ItinerarySegmentType } from '../../../models';
import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
} from '../../../stores';
import ItineraryIcon from '../ItineraryIcon.vue';

import ItineraryLocation from './ItineraryLocation.vue';

interface ItineraryLocationSegmentProps {
  segment: ItineraryLocationSegment | ItineraryLocationLoaderSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryLocationSegmentProps>();

const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const editTripStore = useEditTripStore();

function onTrashCanClick(ingredientId: IngredientId): void {
  tripOverviewModalsStore.openRemoveDestinationModal(ingredientId);
}

function changeNumberOfNights(
  segment: ItineraryLocationSegment | ItineraryLocationLoaderSegment,
  newNumberOfNights: number
): void {
  if (segment.segmentType === ItinerarySegmentType.MiddleLocation) {
    editTripStore.changeNumberOfNights(segment.ingredientId, newNumberOfNights);
  }
}

function changeLocation(): void {
  if (!props.printMode) {
    tripOverviewPageStore.changeLocation(props.segment);
  }
}
</script>

<template>
  <div class="itinerary-location-segment">
    <button
      class="itinerary-location-segment-grid"
      :class="{
        'simple-location':
          segment.segmentType === ItinerarySegmentType.StartLocation ||
          segment.segmentType === ItinerarySegmentType.EndLocation,
        'print-mode': props.printMode,
      }"
      @click.stop="changeLocation"
    >
      <ItineraryIcon
        class="itinerary-icon"
        :segment-type="props.segment.segmentType"
      />
      <ItineraryLocation
        :location="segment.location.name"
        :country="segment.location.country"
      />
      <AppSvgIcon
        v-if="!props.printMode"
        class="edit-icon"
        name="pencil"
        size="24"
      />
      <button
        v-if="segment.segmentType === ItinerarySegmentType.MiddleLocation"
        class="nights-selector-desktop no-parent-hover"
        @click.stop="
          statsigEventsStore.logCoreNightsSelectorOpened({
            source: CoreEventSource.TripOverview,
          })
        "
      >
        <NightsSelector
          :number-of-nights="segment.numberOfNights"
          app-select-type="transparent"
          app-select-size="size-l"
          :flicker-margin="{ xSpacing: 'spacing-8' }"
          :disabled="props.printMode"
          @change-number-of-nights="changeNumberOfNights(segment, $event)"
        />
      </button>
      <AppTrashCanButton
        v-if="
          segment.segmentType === ItinerarySegmentType.MiddleLocation &&
          !props.printMode
        "
        class="no-parent-hover"
        :aria-label="$t('a11y.remove_destination')"
        @click.stop="onTrashCanClick(segment.ingredientId)"
      />
    </button>
    <div
      v-if="segment.segmentType === ItinerarySegmentType.MiddleLocation"
      class="nights-selector-mobile"
    >
      <NightsSelector
        :number-of-nights="segment.numberOfNights"
        :disabled="props.printMode"
        app-select-type="transparent"
        app-select-size="size-l"
        @change-number-of-nights="changeNumberOfNights(segment, $event)"
        @open-number-of-nights="
          statsigEventsStore.logCoreNightsSelectorOpened({
            source: CoreEventSource.TripOverview,
          })
        "
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.itinerary-location-segment {
  margin: 0 var(--spacing-24) var(--spacing-24) var(--spacing-24);
  border: 1px solid var(--color-neutral-30);
  border-radius: var(--border-radius-12);
  background-color: var(--color-white);
  position: relative;

  .itinerary-location-segment-grid {
    all: unset;
    width: -webkit-fill-available;
    border-radius: var(--border-radius-12);
    transition: var(--transition-very-short);
    padding: var(--spacing-16) var(--spacing-8);

    display: grid;
    column-gap: var(--spacing-8);
    row-gap: var(--spacing-24);
    grid-template-columns:
      var(--spacing-32) auto var(--spacing-32) min-content
      var(--spacing-32);
    grid-template-rows: var(--spacing-32);
    align-items: center;

    &.simple-location {
      grid-template-columns: var(--spacing-32) auto var(--spacing-32);
    }

    &.print-mode {
      .nights-selector-desktop {
        border-left: none;
        border-right: none;
      }
    }

    &.hide-pencil-icon {
      grid-template-columns:
        var(--spacing-32) auto min-content
        var(--spacing-32);

      .edit-icon {
        display: none;
      }
    }

    &:not(.print-mode) {
      @include for-more-or-equal-than-599-px {
        @include on-hover() {
          /* stylelint-disable-next-line selector-pseudo-class-disallowed-list */
          &:not(:has(.no-parent-hover:hover)) {
            background: var(--color-neutral-20);
            cursor: pointer;
          }
        }
      }
    }

    .nights-selector-desktop {
      all: unset;
      height: 100%;
      display: flex;
      align-items: center;
      border-left: 1px solid var(--color-neutral-30);
      border-right: 1px solid var(--color-neutral-30);
    }

    @include for-less-or-equal-than-599-px {
      grid-template-columns: var(--spacing-32) auto var(--spacing-32);
      grid-template-rows: var(--spacing-32);

      .edit-icon,
      .nights-selector-desktop {
        display: none;
      }

      .nights-selector-mobile {
        display: block;
        grid-column: 3 / auto;
      }
    }
  }

  .nights-selector-mobile {
    display: none;
  }

  @include for-less-or-equal-than-599-px {
    margin: 0 var(--spacing-12) var(--spacing-24) var(--spacing-12);

    .nights-selector-mobile {
      padding: var(--spacing-12) var(--spacing-8);
      display: flex;
      align-items: center;
      justify-content: flex-start;
      border-top: 1px solid var(--color-neutral-30);
      height: 47px;
    }
  }

  &::after {
    content: '';
    height: 25px;
    width: 0px;
    border-right: 1px dashed var(--color-neutral-70);
    position: absolute;
    bottom: -25px;
    left: 24px;
  }
}
</style>
